import Tree, { deepClone } from "./Tree";
import api from "./../api";
import { Direction, Preferences, TreeDefaultNodeIds } from "../../utils/constants";
import { ProcessItemTypes } from "../../utils/icons";
import { RedZoneHelper } from "../helpers/RedZoneHelper";

let redZoneHelper = null;
export default {
  namespaced: true,
  state: {
    persistTimeout: null,
    tree: null,
    activeGrid: {
      id: null,
      editMode: false,
    },
    visibleItemIds: [],
    isLoading: false,
    hashTable: {}, // for fast access to process data
    showTrash: false,
    selectedTrashItems: [],
  },
  mutations: {},
  actions: {
    recalculateHashTable({ commit, state, dispatch }) {
      state.hashTable = state.tree.getHashTable(state.tree.allData);
    },
    setActiveItems({ commit, state, dispatch }, id) {
      if (!id) id = TreeDefaultNodeIds.Root;

      state.tree.setActiveSubTree(id);
      dispatch("calculateVisibleItemIds");
      dispatch("setActiveItem", { id });
    },
    async load({ commit, state, dispatch, getters }, { id }) {
      try {
        state.isLoading = true;

        const resp = await api.get(`grids`);
        state.tree = new Tree(resp.data);
        const node = state.tree.getNodeByItemId(state.tree.allData, id);

        await dispatch("recalculateHashTable");
        await dispatch("calculateVisibleItemIds");
        await dispatch("setActiveItem", { id: node?.id, itemId: node?.itemId });
        dispatch("smartSearch/loadGrids", null, { root: true });
        redZoneHelper = new RedZoneHelper({ state, dispatch, getters });
        dispatch("pdesign/setOpenedItem", { id: node?.id, itemId: node?.itemId }, { root: true });

        state.isLoading = false;
      } catch (error) {
        console.log(error);
        state.isLoading = false;
      }
    },
    async loadTrash({ commit, state, dispatch }) {
      dispatch("setActiveItems", TreeDefaultNodeIds.Trash);
      state.tree.setValue(TreeDefaultNodeIds.Trash, "hide", false);
    },
    toggleTrash({ commit, state, dispatch }, value) {
      if (value === undefined) value = !state.showTrash;
      state.showTrash = value;
    },
    setSelectedTrashItems({ commit, state, dispatch }, value) {
      state.selectedTrashItems = [...value];
    },
    toggleExpanded(_, { id, itemId, expanded = null }) {
      redZoneHelper.toggleExpanded({ id, itemId, expanded });
    },
    addFolder(_, id) {
      redZoneHelper.addFolder(id);
    },
    async addGrid(_, id) {
      redZoneHelper.addGrid(id);
    },

    async remove(_, { ids }) {
      redZoneHelper.remove({ ids });
    },
    async removePermanently({ state, dispatch }, { ids }) {
      let nodes = [];
      ids.forEach((id) => {
        const node = { ...state.tree.getNode(state.tree.allData, id) };
        if (!node.shared) {
          nodes.push(node);
        }
      });

      const itemIds = nodes.map((node) => node.itemId);
      if (nodes.length > 0) {
        await dispatch("pdesigns/delete", itemIds, { root: true });
        nodes.forEach(async (node) => {
          await dispatch("removeNode", { nodeId: node.id, basePath: "grids" });
        });
      }
      redZoneHelper.removePermanently({ ids });

      dispatch("smartSearch/loadGrids", null, { root: true });
      dispatch(
        "preferences/toggleFavorites",
        { type: Preferences.FavoriteProcesses, ids, isDelete: true },
        { root: true }
      );
    },
    restore({ dispatch }, { ids }) {
      redZoneHelper.restore({ ids });
      dispatch("smartSearch/loadGrids", null, { root: true });
    },
    open(_, id) {
      redZoneHelper.open(id);
    },
    async rename(_, { id, itemId, title }) {
      redZoneHelper.rename({ id, itemId, title, basePath: "grids" });
    },
    async renameTeam(_, { id, itemId, title }) {
      redZoneHelper.renameTeam({ id, itemId, title });
    },
    async persist({ commit, state, dispatch }, teamIds = null) {
      clearTimeout(state.persistTimeout);
      state.persistTimeout = setTimeout(async () => {
        try {
          const activeItem = state.tree.getNode(state.tree.allData, state.activeGrid.id);
          const activeTeamIds = Array.isArray(teamIds) ? teamIds : [teamIds || activeItem.parents[1]];
          const payload = [];

          activeTeamIds.forEach((activeTeamId) => {
            if (activeTeamId === TreeDefaultNodeIds.Trash) return;

            let list = deepClone(state.tree.getNode(state.tree.allData, activeTeamId));
            let trashList = deepClone(state.tree.getNode(state.tree.allData, `TRASH_${activeTeamId}`) || "");

            state.tree.removeMetaData(list);
            trashList && state.tree.removeMetaData(trashList);

            payload.push({ list, trash_list: trashList, team_id: activeTeamId });
          });

          // update the Grid list.
          await api.patch("grids", payload);
        } catch (error) {
          console.log(error);
        }
      }, 500);
    },
    setActiveItem({ commit, state, dispatch }, { id, itemId, editMode = false }) {
      if (editMode) {
        dispatch("resetActiveContextMenu", null, { root: true });
      }
      state.activeGrid = {
        id,
        itemId: itemId ?? id,
        editMode,
      };
    },
    resetActiveItem({ commit, state, dispatch }) {
      state.activeGrid = {
        id: null,
        itemId: null,
        editMode: false,
      };
    },
    // move active navigation item up and down
    moveActiveItem({ commit, getters, state, dispatch }, direction) {
      if (!state.activeGrid.id) {
        const id = state.visibleItemIds[0];
        const itemId = state.tree.getNode(state.tree.allData, id).itemId;
        return dispatch("setActiveItem", { id, itemId });
      }

      let index = state.visibleItemIds.findIndex((item) => item === state.activeGrid.id);
      if (direction === Direction.Up) index = index > 0 ? index - 1 : 0;
      else index = index < state.visibleItemIds.length - 1 ? index + 1 : index;

      const newElementId = state.visibleItemIds[index];
      const itemId = state.tree.getNode(state.tree.allData, newElementId).itemId;

      const element = document.querySelector(`.kk-grids__tree [data-id="${newElementId}"]`);
      element.scrollIntoView({ block: "center" });

      dispatch("setActiveItem", { id: newElementId, itemId });
    },
    calculateVisibleItemIds({ commit, state, dispatch }) {
      state.visibleItemIds = state.tree.getVisibleNodeIds(state.tree.activeSubTree);
    },
    moveNode(_, { id, direction }) {
      redZoneHelper.moveNode({ id, direction });
    },
    moveNodeOneLevel(_, { id, direction }) {
      redZoneHelper.moveNodeOneLevel({ id, direction });
    },
    moveGrid({ commit, state, dispatch, getters }, { id, position, targetId }) {
      if (!getters.isMoveAllowed(id, targetId)) return false;

      state.tree.moveNode(id, position, targetId);

      dispatch("calculateVisibleItemIds");
      dispatch("persist");
    },
    async syncPermissions(_, { designId, updatedPermissions }) {
      await redZoneHelper.syncPermissions({ designId, updatedPermissions, basePath: "grids" });
    },
    async addNode(_, { teamId, node, email }) {
      await redZoneHelper.addNode({ teamId, node, email, basePath: "grids" });
    },
    async resendInvitation(_, { designId, nodeId }) {
      await redZoneHelper.resendInvitation({ designId, nodeId, basePath: "grids" });
    },
    async renameNode(_, { nodeId, newTitle }) {
      await redZoneHelper.renameNode({ nodeId, newTitle, basePath: "grids" });
    },
    async removeNode(_, { nodeId, teamId, email }) {
      await redZoneHelper.removeNode({ nodeId, teamId, email, basePath: "grids" });
    },
  },
  getters: {
    teamId: (state) => (id) => {
      const activeItem = state.tree.getNode(state.tree.allData, id);
      let teamId = activeItem?.parents[1] ?? id;
      if (TreeDefaultNodeIds.Trash === teamId) {
        teamId = activeItem.reference[1];
      }
      return teamId;
    },
    getTeamDesigns: (state) => (teamId) => {
      const node = state.tree?.getNode(state.tree.allData, teamId);
      return state.tree.getAllPropertiesInSubTree(
        node,
        ["id", "title"],
        (node) => node.type !== ProcessItemTypes.Folder
      );
    },
    getGridById: (state) => (id) => {
      return state.tree?.getNode(state.tree.allData, id);
    },
    getById: (state) => (id) => {
      return state.tree?.getNode(state.tree.allData, id);
    },
    getTrashItems: (state, getters) => () => {
      const items = [];
      const trash = getters.getGridById(TreeDefaultNodeIds.Trash);
      trash.children.forEach((item) => {
        items.push(...item.children);
      });

      return items;
    },
    isEditAllowed: (state) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);

      if (node.parents.length === 1) return false;
      return true;
    },
    isMoveUpAllowed: (state, getters) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);
      // Take into account the active subtree. the first two levels in sub tree are not allowed to move up
      if (
        node.parents.length <= 2 ||
        node.parents[1] === TreeDefaultNodeIds.Trash ||
        getters.subTreeParents(id).length === 1
      )
        return false;
      return true;
    },
    // used for move one level down and moving item up and down
    isMoveDownAllowed: (state, getters) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);

      if (node.parents.length === 1 || node.parents[1] === TreeDefaultNodeIds.Trash || getters.subTreeParents(id) === 0)
        return false;
      return true;
    },
    // parents of node with regards to active sub tree
    subTreeParents: (state) => (id) => {
      const node = state.tree.getNode(state.tree.allData, id);

      const index = node.parents.indexOf(state.tree.activeSubTree.id);
      const subTreeParents = index !== -1 ? node.parents.slice() : [];

      return subTreeParents;
    },
    // used when item is dragged and dropped
    isMoveAllowed: (state) => (id, targetId) => {
      const node = state.tree.getNode(state.tree.allData, id);
      const target = state.tree.getNode(state.tree.allData, targetId);

      if (node.parents[1] !== target.parents[1]) return false;

      return true;
    },
    search: (state) => (searchTerm) => {
      const items = state.tree?.filter(
        (item) => item.title.toLowerCase().includes(searchTerm.toLowerCase()) && item.type !== ProcessItemTypes.Folder
      );
      return items;
    },
  },
};
